import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getRichContentMessageForTextingStep } from '../../../../../models/UiUtils';
import { ImpersonationContextViewModel } from '../../../../../viewmodels/AdminViewModels';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { StepSendFromOption } from '../../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { AdvancedOptionsButton } from '../../../../components/automation/AutomationStepCard/presentation';
import { styleSheet } from '../../../../components/automation/AutomationStepCard/styles';
import { AutomationTagAutocomplete } from '../../../../components/automation/AutomationTriggerCard/presentation';
import { EditAutomationStepEmailComposerModal } from '../../../../components/automation/EditAutomationStepEmailComposerModal';
import { Checkbox } from '../../../../components/Checkbox';
import { Collapsible } from '../../../../components/Collapsible';
import { InputFieldError } from '../../../../components/InputFieldError';
import { TextInput } from '../../../../components/TextInput';
import { bs } from '../../../../styles/styles';
import { ConditionalFilter } from '../../components/ConditionalFilter';
import { ConditionalOptionsSection } from '../../components/ConditionalOptionsSection';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { useBehaviorConditionalOptions } from '../../hooks/useBehaviorConditionalOptions';
import { useEmailStepSave } from '../../hooks/useEmailStepSave';
import { useEmailStepState } from '../../hooks/useEmailStepState';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';

interface EmailStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function EmailStepRenderer({ nodeId, data, readOnly: readOnlyProp }: EmailStepRendererProps) {
	const {
		step: emailStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<Api.IEmailAutomationStep>(data, readOnlyProp);

	const { updateNodeData, connectionTypes } = useAutomationFlowContext();
	const userSession = useUserSession();

	const impersonationContextViewModel = React.useMemo(
		() => (impersonationContext ? new ImpersonationContextViewModel(impersonationContext) : undefined),
		[impersonationContext]
	);

	const stepType = data.stepType;
	const schedule = emailStep?.schedule || stepData?.schedule;

	const [showEmailEditor, setShowEmailEditor] = React.useState(false);

	const {
		name,
		setName,
		tagToAddOnClick,
		setTagToAddOnClick,
		showEmailLinkClickTags,
		setShowEmailLinkClickTags,
		skipStepContactFilter,
		setSkipStepContactFilter,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		showRequiredFieldError,
		autoSave,
		clearInlineErrorMessage,
	} = useEmailStepState({
		emailStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const syncStepToNodeData = React.useCallback(
		(updatedStep: Partial<Api.IEmailAutomationStep>) => {
			if (!updateNodeData || !emailStep) {
				return;
			}
			updateNodeData(nodeId, {
				automationStep: {
					...emailStep,
					...updatedStep,
				},
			});
		},
		[updateNodeData, nodeId, emailStep]
	);

	const { save } = useEmailStepSave({
		emailStep,
		stepId,
		templateId: template?.id,
		name,
		tagToAddOnClick,
		skipStepContactFilter,
		autoSave,
	});

	const onNameInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextName = e.target.value;
		setName(nextName);
		clearInlineErrorMessage();
		// Keep node header/compact title in sync while typing.
		syncStepToNodeData({ name: nextName });
	};

	const onEmailCtaClicked = () => {
		setShowEmailEditor(true);
		clearInlineErrorMessage();
	};

	const onTagForLinkClickChanged = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
		syncStepToNodeData({ tagToAddOnClick: tag });
	};

	const onAddTagToLinkClickCheckChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setShowEmailLinkClickTags(checked);
		if (!checked) {
			setTagToAddOnClick('');
			save({ newTagToAddOnClick: '' });
			syncStepToNodeData({ tagToAddOnClick: '' });
		}
	};

	const onEmailComposerModalRequestClose = (result?: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep) => {
		if (result) {
			syncStepToNodeData(result as Partial<Api.IEmailAutomationStep>);
		}
		setShowEmailEditor(false);
	};

	const saveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		save({ newSendFrom: option, newSendToSender: sendToSender });
		syncStepToNodeData({
			sendFromOptions: option,
			options: {
				...emailStep?.options,
				sendToSender,
			},
		});
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const behaviorConditionalOptions = useBehaviorConditionalOptions({
		nodeId,
		step: emailStep as Api.IAutomationStep | null,
		template,
		readonly,
		parentCaseInfo: data.parentCaseInfo,
		impersonationContext,
	});

	const hasEmailContent = !!emailStep?.content;
	const hasName = !!name;
	const errorMessage =
		showRequiredFieldError && (!hasName || !hasEmailContent)
			? 'Please provide email content and description/name.'
			: null;

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='detailed'
				lastSavedAt={autoSave.lastSavedAt}
				errorMessage={autoSave.errorMessage}
			/>
			{emailStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={emailStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
					impersonationContext={impersonationContext}
				/>
			)}
			<div className={css(styleSheet.fieldLabel)}>
				Email title: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<TextInput
				className={css(styleSheet.emailNameInput)}
				disabled={!canEdit}
				inputId={`automation-email-step-name-input-${emailStep?.id || nodeId}`}
				key={`${nodeId}-name-input`}
				onBlur={() => {
					save({ newName: name });
					syncStepToNodeData({ name });
				}}
				onChange={onNameInputChanged}
				onFocus={clearInlineErrorMessage}
				placeholder='Enter a brief description of this email'
				type='text'
				value={name || ''}
			/>
			<InputFieldError errorMessage={errorMessage}>
				<button
					className={css(
						styleSheet.emailCta,
						!hasEmailContent ? styleSheet.emailCreateButton : styleSheet.emailEditButton
					)}
					disabled={!emailStep || !template}
					onClick={onEmailCtaClicked}
				>
					<span>
						{!emailStep
							? 'Creating step...'
							: readonly
								? 'Preview Email'
								: `${hasEmailContent ? 'Preview / Edit' : 'Create'} Email`}
					</span>
				</button>
			</InputFieldError>
			<ConditionalOptionsSection nodeId={nodeId} {...behaviorConditionalOptions} />
			<AdvancedOptionsButton
				isCollapsed={!advancedOptionsToggled}
				onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
			/>
			<Collapsible isCollapsed={!advancedOptionsToggled}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={emailStep?.id ? `automation-add-tag-on-link-click-checkbox-${emailStep.id}` : uuid()}
						checked={showEmailLinkClickTags}
						type='large'
						disabled={readonly}
						onChange={onAddTagToLinkClickCheckChanged}
					>
						<div>
							<div className={css(styleSheet.checkboxText)}>
								Add a tag if the recipient clicks (any) link in the email
							</div>
						</div>
					</Checkbox>
					{showEmailLinkClickTags && (
						<AutomationTagAutocomplete
							className={css(styleSheet.tagAutomcomplete)}
							limit={5}
							onTagSelected={onTagForLinkClickChanged}
							initialTag={tagToAddOnClick}
						/>
					)}
					{emailStep && (
						<StepSendFromOption
							showInternalSendFromOption
							step={emailStep}
							isBusy={false}
							onSave={saveSenderOptions}
							connectionTypes={connectionTypes}
							impersonationContext={impersonationContextViewModel}
							canEdit={canEdit}
						/>
					)}
					<ConditionalFilter
						stepId={stepId || nodeId}
						filter={skipStepContactFilter}
						canEdit={canEdit}
						isBusy={false}
						onFilterChange={handleFilterChange}
					/>
				</div>
			</Collapsible>
			{showEmailEditor && emailStep && template && (
				<EditAutomationStepEmailComposerModal
					automationTemplateId={template.id}
					step={emailStep}
					impersonationContext={impersonationContext}
					onRequestClose={onEmailComposerModalRequestClose}
					onStepChanged={() => {
						// No-op: step changes are tracked via auto-save
					}}
					onAttachmentRemoved={() => {
						// No-op: attachment removal is handled by the modal internally
					}}
					readonly={readonly}
					trigger={trigger}
					defaultInitialContent={Api.createRawRichTextContentStateWithText(
						getRichContentMessageForTextingStep(userSession, trigger, emailStep.schedule)
					)}
				/>
			)}
		</>
	);
}
